import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Calculator, TrendingUp, Download, Share2, BookOpen, AlertTriangle, Check } from 'lucide-react';
import { Badge } from '../ui/badge';
import jsPDF from 'jspdf';

const QuitterMicroSimulator = () => {
  const [inputs, setInputs] = useState({
    caActuel: '60000',
    caPrevisionnel: '90000',
    charges: '15000',
    activite: 'services'
  });
  const [results, setResults] = useState({});

  const SEUILS = {
    services: { seuil: 77700, label: 'Prestations de services' },
    commerce: { seuil: 188700, label: 'Achat-revente / Commerce' }
  };

  const calculate = () => {
    const caActuel = parseFloat(inputs.caActuel) || 0;
    const caPrev = parseFloat(inputs.caPrevisionnel) || 0;
    const charges = parseFloat(inputs.charges) || 0;
    const seuil = SEUILS[inputs.activite].seuil;
    const benefice = caPrev - charges;

    const depasseSeuil = caActuel >= seuil || caPrev >= seuil;
    const pourcentageSeuil = (caActuel / seuil * 100).toFixed(0);
    const margeAvantSeuil = seuil - caActuel;

    // En micro
    const micro_cotis = caPrev * 0.256;
    const micro_abattement = caPrev * 0.34;
    const micro_imposable = caPrev - micro_abattement;
    let micro_ir = 0;
    if (micro_imposable > 29579) micro_ir += (Math.min(micro_imposable, 84577) - 29579) * 0.30;
    if (micro_imposable > 11600) micro_ir += (Math.min(micro_imposable, 29579) - 11600) * 0.11;
    const micro_net = caPrev - micro_cotis - Math.max(0, micro_ir);

    // En SASU
    const sasu_salaire = benefice * 0.45;
    const sasu_cp = sasu_salaire * 0.42;
    const sasu_cs = sasu_salaire * 0.22;
    const sasu_net_sal = sasu_salaire - sasu_cs;
    const sasu_resultat = benefice - sasu_salaire - sasu_cp;
    let sasu_is = 0;
    if (sasu_resultat > 0) sasu_is = Math.min(sasu_resultat, 42500) * 0.15 + Math.max(sasu_resultat - 42500, 0) * 0.25;
    const sasu_div = Math.max(0, sasu_resultat - sasu_is) * 0.70;
    const sasu_net = sasu_net_sal + sasu_div;

    // En EURL
    const eurl_remun = benefice * 0.50;
    const eurl_cotis = eurl_remun * 0.45;
    const eurl_net_rem = eurl_remun - eurl_cotis;
    const eurl_resultat = benefice - eurl_remun - eurl_cotis;
    let eurl_is = 0;
    if (eurl_resultat > 0) eurl_is = Math.min(eurl_resultat, 42500) * 0.15 + Math.max(eurl_resultat - 42500, 0) * 0.25;
    const eurl_div = Math.max(0, eurl_resultat - eurl_is) * 0.70;
    const eurl_net = eurl_net_rem + eurl_div;

    const meilleureAlternative = sasu_net > eurl_net ? 'SASU' : 'EURL';
    const meilleureNetAlternative = Math.max(sasu_net, eurl_net);
    const gainTransition = meilleureNetAlternative - micro_net;

    const devraitQuitter = depasseSeuil || gainTransition > 2000 || (charges / caPrev > 0.20);

    setResults({
      depasseSeuil, pourcentageSeuil, margeAvantSeuil: Math.round(margeAvantSeuil),
      seuil, micro_net: Math.round(micro_net),
      sasu_net: Math.round(sasu_net), eurl_net: Math.round(eurl_net),
      meilleureAlternative, meilleureNetAlternative: Math.round(meilleureNetAlternative),
      gainTransition: Math.round(gainTransition),
      devraitQuitter,
      raisons: []
    });
  };

  useEffect(() => { calculate(); }, [inputs]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const shareLink = () => {
    const url = window.location.href;
    if (navigator.share) { navigator.share({ title: 'Quand quitter la micro - Bozzam', url }); }
    else { navigator.clipboard.writeText(url); alert('Lien copié !'); }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFillColor(30, 64, 175); doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255); doc.setFontSize(20);
    doc.text('Bozzam - Quand quitter la micro ?', 15, 25);
    doc.setFontSize(10); doc.text('blog.bozzam.com', 15, 35);
    doc.setTextColor(0); let y = 55;
    doc.setFontSize(11);
    doc.text(`CA actuel : ${parseInt(inputs.caActuel).toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`CA prév. : ${parseInt(inputs.caPrevisionnel).toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Seuil micro : ${results.seuil?.toLocaleString()} EUR`, 15, y); y += 10;
    doc.text(`Net en micro : ${results.micro_net?.toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Net en SASU : ${results.sasu_net?.toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Net en EURL : ${results.eurl_net?.toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Recommandation : ${results.devraitQuitter ? 'Quitter la micro' : 'Rester en micro'}`, 15, y); y += 15;
    doc.setFontSize(9); doc.setTextColor(100);
    doc.text('Sources : URSSAF 2026, CGI, service-public.fr', 15, y);
    doc.save('simulation-quitter-micro-bozzam.pdf');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" /> Quand quitter la micro ?
            </CardTitle>
            <CardDescription>Analysez si c'est le bon moment de changer de statut</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>CA actuel / dernière année (€)</Label>
              <Input data-testid="ca-actuel-input" type="text" inputMode="numeric" value={inputs.caActuel}
                onChange={(e) => handleInputChange('caActuel', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="60000" className="mt-1" />
            </div>
            <div>
              <Label>CA prévisionnel prochaine année (€)</Label>
              <Input data-testid="ca-prev-input" type="text" inputMode="numeric" value={inputs.caPrevisionnel}
                onChange={(e) => handleInputChange('caPrevisionnel', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="90000" className="mt-1" />
            </div>
            <div>
              <Label>Charges / frais réels annuels (€)</Label>
              <Input data-testid="charges-input" type="text" inputMode="numeric" value={inputs.charges}
                onChange={(e) => handleInputChange('charges', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="15000" className="mt-1" />
            </div>
            <div>
              <Label>Type d'activité</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {Object.entries(SEUILS).map(([key, val]) => (
                  <Button key={key} data-testid={`activite-${key}-btn`}
                    variant={inputs.activite === key ? 'default' : 'outline'}
                    className={inputs.activite === key ? 'bg-blue-600 hover:bg-blue-700' : ''}
                    onClick={() => handleInputChange('activite', key)}>
                    {val.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Jauge seuil */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>0€</span>
                <span>Seuil : {SEUILS[inputs.activite].seuil.toLocaleString()}€</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className={`h-4 rounded-full transition-all ${parseInt(results.pourcentageSeuil) >= 100 ? 'bg-red-500' : parseInt(results.pourcentageSeuil) >= 80 ? 'bg-amber-500' : 'bg-green-500'}`}
                  style={{ width: `${Math.min(parseInt(results.pourcentageSeuil) || 0, 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-center mt-1 text-gray-600">{results.pourcentageSeuil}% du seuil</p>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <BookOpen className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Sources légales 2026</p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>Seuils micro 2026 : 77 700€ services / 188 700€ commerce</li>
                    <li>URSSAF micro BNC : 25.6%, BIC : 21.2%</li>
                    <li>IS : 15% ({'<'}42 500€) / 25% (au-delà)</li>
                    <li>service-public.fr | entreprendre.service-public.fr</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Analyse</CardTitle>
            <Badge className={results.devraitQuitter ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}>
              {results.devraitQuitter ? 'Transition recommandée' : 'Restez en micro'}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-6">
            {results.depasseSeuil && (
              <div className="p-4 bg-red-50 rounded-lg border-2 border-red-200 flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-red-800">Dépassement du seuil micro !</p>
                  <p className="text-xs text-red-700">Votre CA dépasse le seuil de {results.seuil?.toLocaleString()}€. Le changement de statut est obligatoire.</p>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <div className={`p-4 rounded-lg border-2 ${!results.devraitQuitter ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Micro-entreprise</span>
                  {!results.devraitQuitter && <Badge className="bg-green-100 text-green-800">Optimal</Badge>}
                </div>
                <p className="text-2xl font-black text-gray-900 mt-1" data-testid="result-micro-net">{results.micro_net?.toLocaleString()}€ <span className="text-sm font-normal text-gray-500">net/an</span></p>
              </div>

              <div className={`p-4 rounded-lg border-2 ${results.devraitQuitter && results.meilleureAlternative === 'SASU' ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex justify-between items-center">
                  <span className="font-semibold">SASU</span>
                  {results.devraitQuitter && results.meilleureAlternative === 'SASU' && <Badge className="bg-green-100 text-green-800">Recommandé</Badge>}
                </div>
                <p className="text-2xl font-black text-gray-900 mt-1">{results.sasu_net?.toLocaleString()}€ <span className="text-sm font-normal text-gray-500">net/an</span></p>
              </div>

              <div className={`p-4 rounded-lg border-2 ${results.devraitQuitter && results.meilleureAlternative === 'EURL' ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex justify-between items-center">
                  <span className="font-semibold">EURL</span>
                  {results.devraitQuitter && results.meilleureAlternative === 'EURL' && <Badge className="bg-green-100 text-green-800">Recommandé</Badge>}
                </div>
                <p className="text-2xl font-black text-gray-900 mt-1">{results.eurl_net?.toLocaleString()}€ <span className="text-sm font-normal text-gray-500">net/an</span></p>
              </div>
            </div>

            <div className={`p-4 rounded-lg border ${results.gainTransition > 0 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
              <p className="text-sm text-gray-800">
                {results.gainTransition > 0
                  ? <><span className="font-bold">Gain potentiel en passant en {results.meilleureAlternative} :</span> +{results.gainTransition?.toLocaleString()}€/an</>
                  : <><span className="font-bold">La micro reste avantageuse</span> : vous perdriez {Math.abs(results.gainTransition)?.toLocaleString()}€/an en changeant</>
                }
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <Button onClick={shareLink} variant="outline" data-testid="share-btn"><Share2 className="h-4 w-4 mr-2" /> Partager</Button>
              <Button onClick={exportPDF} variant="outline" data-testid="export-btn"><Download className="h-4 w-4 mr-2" /> PDF</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}>Bozzam App</Button>
            </div>
            <div className="text-xs text-gray-500 text-center pt-2 border-t">
              <p>Sources : URSSAF 2026, CGI, service-public.fr</p>
              <p>Seuils micro 2026 | entreprendre.service-public.fr</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QuitterMicroSimulator;
